import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ChangeDetectionStrategy, computed, signal, inject } from "@angular/core";
import { flatten, uniqBy } from "lodash";
import { LoaderComponent } from "rl-common/components/panel/loader/loader.component";
import { APKeyValueOption } from "rl-common/models/ap-key-value-option";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { IHierarchyLabelContext } from "rl-common/models/i-hierarchy-label-context";
import { CharTypeId } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRule } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { GrowlerService } from "rl-common/services/growler.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Observable, Subscription, defer, forkJoin } from "rxjs";
import { filter, finalize, map, switchMap, tap } from "rxjs/operators";
import { AccountGroupRuleComponent } from "../account-group-rule/account-group-rule.component";
import { AccountingProcesses } from "../accounting-processes.consts";

@Component({
	selector: "rl-account-group-rules",
	templateUrl: "./account-group-rules.component.html",
	styleUrls: ["./account-group-rules.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AccountGroupRuleComponent, LoaderComponent]
})

export class AccountGroupRulesComponent implements OnInit, OnDestroy {
	private readonly _accountingProcessesService = inject(AccountingProcessesService);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _entityConfigService = inject(EntityConfigService);
	private readonly _promptService = inject(PromptService);
	private readonly _growlerService = inject(GrowlerService);

	@Input()
	accountGroup: IAccountingProcessAccountGroup;

	/** rules hang off the Set, not the Account Group; the group is still needed for template lookups */
	@Input()
	ruleSet: IAccountingProcessAccountGroupRuleSet;

	/** owned by the Account Group so every Set under it shares one HLC per LOV */
	@Input()
	hlcCache = new Map<string, IHierarchyLabelContext>();

	/** just the rule: the Set is supplied by the parent's template, which already has it in scope */
	@Output()
	edit = new EventEmitter<IAccountingProcessAccountGroupRule>();

	rules: IAccountingProcessAccountGroupRule[] = [];

	/** contact-side picklists, off the templates the Account Group's party can use -- same two lists the build modal keeps */
	contactPicklistItems: ICharacteristicMetaData[] = [];

	/** amount-side picklists, restricted to the three whitelisted fields (see the consts) */
	amountPicklistItems: ICharacteristicMetaData[] = [];

	templateItems: APKeyValueOption[] = [];
	templateGroupItems: APKeyValueOption[] = [];

	loadRules$: Observable<IAccountingProcessAccountGroupRule[]>;
	_subs: Subscription[] = [];

	private readonly _isLoadingRules = signal(false);
	private readonly _isLoadingConfig = signal(false);

	// Gates the rows on BOTH fetches, not just the rules. The rules arrive from one request and the
	// picklist/template config a rule's labels are resolved from arrives from others, so rendering as
	// soon as the rules land shows rows whose Picklist Field is momentarily blank and then corrects
	// itself. One rule, one appearance: nothing renders until every input it reads is present.
	readonly isLoading = computed(() => this._isLoadingRules() || this._isLoadingConfig());

	ngOnInit(): void {
		// Amount fields ride along in the OneConfig payload, so no fetch -- and no dependence on the
		// Account Group's deal templates, which is why this sits here and not in loadTemplateItems()
		// (that returns early for a group with no templates, and an Amount rule still needs its label).
		this.amountPicklistItems = AccountingProcesses.amountRuleCmds(this._oneConfig)
			.sort((a, b) => a.label.localeCompare(b.label));

		this.loadTemplateItems();
		this.loadRules();
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

	loadRules(): void {
		// no sort: rules within a Set are unordered -- a Set matches only when ALL of them match
		// defer, so the spinner is raised on every SUBSCRIPTION rather than once here: deleteRule and
		// the parent's reloadRulesForSet both re-subscribe to this same observable, and setting the
		// flag outside would leave those refreshes silent.
		this.loadRules$ = defer(() => {
			this._isLoadingRules.set(true);
			return this._accountingProcessesService.getAccountGroupRules(this.ruleSet.groupRuleSetId);
		}).pipe(
			tap(rules => this.rules = rules),
			// finalize, not a second tap: an errored fetch has to clear the spinner too, otherwise the
			// Set spins forever with no indication anything went wrong
			finalize(() => this._isLoadingRules.set(false))
		);

		this._subs.push(this.loadRules$.subscribe());
	}

	loadTemplateItems() {
		// Nothing to fetch, so the rows are gated on the rules alone -- leaving _isLoadingConfig true
		// here would spin forever.
		if (!this.accountGroup?.dealTemplates?.length) {
			return;
		}
		const templateItems$ = this._entityConfigService.getPartyTemplates(CharTypeId.Deal, this.accountGroup.dealTemplates[0].templateID, this.accountGroup.partyId).pipe(
			map(entities => {
				return entities.map(e => this._oneConfig.getTemplate(e.charTypeId, e.templateId)).filter(t => !!t);
			}),
			map(templates => {
				this.templateItems = templates.map(t => AccountingProcesses.KeyValueOptionMapping.fromTemplate(t));
				return templates;
			}),
			tap(templateItems => {
				const charCollection = templateItems.map(ti => this._oneConfig.getTemplateMetaData(CharTypeId.User, ti.templateID));
				// Sorted the same way the build modal sorts its copy of this list. A rule persists only the
				// value set, and a division routinely points several fields at one set (in div 29 Citizenship,
				// Country, Country (H) and Organized In all share the countries set), so the label is resolved
				// by first match. Two differently-ordered lists therefore disagree about which field a saved
				// rule refers to -- the row read "Citizenship" while the edit modal read "Country".
				this.contactPicklistItems = uniqBy(flatten(charCollection.map(c => c.characteristicMetaDatas.filter(cmd => AccountingProcesses.CharDataValueMapping.isLov(cmd)))), "characteristicID")
					.sort((a, b) => a.label.localeCompare(b.label));
			})
		);

		const templateGroupItems$ = this._entityConfigService.getTemplateGroupList(CharTypeId.User).pipe(
			tap(contactTemplateGroups => {
				this.templateGroupItems = contactTemplateGroups.map(ctg => AccountingProcesses.KeyValueOptionMapping.fromTemplateGroup(ctg));
			})
		);

		// forkJoin rather than two subscriptions, so the flag clears once -- when the LAST of them
		// lands. Clearing per-request would let the rows render against half the config, which is the
		// flash this is here to prevent.
		this._isLoadingConfig.set(true);
		const configSub = forkJoin([templateItems$, templateGroupItems$]).pipe(
			finalize(() => this._isLoadingConfig.set(false))
		).subscribe();

		this._subs.push(configSub);
	}

	editRule(rule: IAccountingProcessAccountGroupRule) {
		this.edit.emit(rule);
	}

	deleteRule(rule: IAccountingProcessAccountGroupRule, deleteTemplate: TemplateRef<unknown>) {
		// no "last rule" guard here -- that moved up to the Rule Set level
		const sub = this._promptService.confirmMini("Confirmation", deleteTemplate).pipe(
			filter(confirmDelete => confirmDelete),
			switchMap(() => this._accountingProcessesService.deleteAccountGroupRule(rule.groupRuleId)),
			switchMap(() => this.loadRules$)
		).subscribe(() => this._growlerService.success().growl("Rule deleted successfully"));

		this._subs.push(sub);
	}
}
